import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import {
  HvFileUploader,
  HvFileUploaderPreview,
  HvWarningText,
  File,
  HvDialog,
  HvDialogTitle,
  HvDialogContent,
} from "@hitachivantara/uikit-react-core";
import { NotificationContext } from "lib/context/NotificationContext";
import { uploadAttachment, deleteAttachment } from "lib/api/attachments";
import { parseStatus as ps, parseStatusMessage as psm } from "lib/utils/forms";
import { getImageVideoUrl } from "lib/utils/url";
import { Attribute } from "lib/api";

const PropertyFileUpload: React.FC<{
  attribute: Attribute;
  replace: (index: number, value: unknown) => void;
  index: number;
}> = ({ attribute, replace, index }) => {
  const { t } = useTranslation("cameraAddEdit");
  const {
    setStatus,
    errors,
    touched,
    status: { serverSideErrors = {} } = {},
  } = useFormikContext<CameraFormData>();

  const { setShowBanner, setBannerMessage, removedCount, setRemovedCount } =
    useContext(NotificationContext);

  const [uploadProgress, setUploadProgress] = useState<ProgressEvent>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [file, setFile] = useState<File>();
  const [open, setOpen] = useState(false);

  const { order, type, contentType, label, value } = attribute;
  const id = (value as string) || "";
  const name = ((type as string) || "").split("|")[1];

  const itemUrl = getImageVideoUrl(id);

  const previewStyle = { maxWidth: "100%", maxHeight: "70vh" };

  const { properties = {} } = serverSideErrors;
  const setServerSizeError = useCallback(
    (message) => {
      properties[order as number] = {
        value: message,
      };
      setStatus({
        serverSideErrors: {
          ...serverSideErrors,
          properties,
        },
      });
    },
    [order, properties, serverSideErrors, setStatus]
  );

  const onErrorHandler = useCallback(() => {
    /* replace(index, {
      ...attribute,
      value: null,
      type: attribute.contentType,
    }); */
    setRemovedCount(removedCount + 1);
  }, [removedCount, setRemovedCount]);

  useEffect(() => {
    if (removedCount) {
      setBannerMessage(t("properties.removed", { count: removedCount }));
      setShowBanner(true);
    }
  }, [t, removedCount, setBannerMessage, setShowBanner]);

  useEffect(() => {
    const previewButtonStyle = { height: 52, width: 52 };
    let f: File | undefined;

    if (value) {
      f = {
        name,
        status: "success",
        preview: (
          <HvFileUploaderPreview
            aria-label={t("properties.upload.openPreviewAriaLabel")}
            onClick={() => setOpen(true)}
          >
            {contentType === "image" && (
              <img
                alt={name}
                style={previewButtonStyle}
                src={itemUrl}
                onError={onErrorHandler}
              />
            )}
            {contentType === "video" && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video style={previewButtonStyle}>
                <source onError={onErrorHandler} src={itemUrl} />
              </video>
            )}
          </HvFileUploaderPreview>
        ),
        // stupid hack just to avoid missing key in list console error
        ...{ id: name },
      };
    } else if (fileName) {
      f = {
        name: fileName,
        status: "success",
        // stupid hack just to avoid missing key in list console error
        ...{ id: fileName },
      };
      if (uploadProgress) {
        const { loaded, total } = uploadProgress;
        f = {
          ...f,
          status: loaded < total ? "progress" : "success",
          progress: loaded,
          size: total,
        };
      }
    }
    setFile(f);
  }, [
    fileName,
    itemUrl,
    name,
    t,
    contentType,
    type,
    uploadProgress,
    value,
    attribute,
    replace,
    onErrorHandler,

    removedCount,
  ]);

  const parseStatus = useCallback(
    (field: string) => ps(field, serverSideErrors, errors, touched),
    [errors, serverSideErrors, touched]
  );

  const parseStatusMessage = useCallback(
    (field: string) => psm(field, serverSideErrors, errors, touched),
    [errors, serverSideErrors, touched]
  );

  const cleanServerSizeError = useCallback(() => {
    delete properties[order as number];
    setStatus({
      serverSideErrors: {
        ...serverSideErrors,
        properties,
      },
    });
  }, [order, properties, serverSideErrors, setStatus]);

  const addFile = useCallback(
    async (f, attr) => {
      const typeFile =
        attr.type === "image" || attr.type === "video"
          ? attr.type
          : attr.contentType;
      const formData = new FormData();
      formData.append(f.name, f);
      if (f.status === "fail") {
        setServerSizeError(f.errorMessage);
        return;
      }
      setFileName(f.name);
      uploadAttachment(formData, {
        onUploadProgress: (progressEvent) => {
          setUploadProgress(progressEvent);
        },
      })
        .then((response) => {
          replace(index, {
            ...attribute,
            contentType: typeFile,
            type: `attachment_id|${f.name}`,
            value: response.data.data.attachment_id,
          });
          cleanServerSizeError();
          setUploadProgress(undefined);
        })
        .catch(() => {
          setFileName(undefined);
          setServerSizeError(t(`validation.errorUploadingFile`));
        });
    },
    [setServerSizeError, replace, attribute, cleanServerSizeError, index, t]
  );

  const removeFile = useCallback(async () => {
    deleteAttachment(id)
      .then(() => {
        setFileName(undefined);
        setFile(undefined);
        replace(index, {
          ...attribute,
          value: null,
          type: attribute.contentType,
        });
        cleanServerSizeError();
      })
      .catch((error) => {
        // status 400 404->499 no file found remove also from camera
        if (
          error.response?.status === 400 ||
          (error.response?.status >= 404 && error.response?.status < 499)
        ) {
          setFileName(undefined);
          setFile(undefined);
          replace(index, {
            ...attribute,
            value: null,
            type: attribute.contentType,
          });
          cleanServerSizeError();
        } else {
          setServerSizeError(t(`validation.errorDeletingFile`));
        }
      });
  }, [
    id,
    replace,
    attribute,
    cleanServerSizeError,
    setServerSizeError,
    index,
    t,
  ]);

  const fileUploaderId = useMemo(
    () => `fileUploader-${contentType || type}-${order}`,
    [order, contentType, type]
  );

  return (
    <>
      <HvDialog open={open} onClose={() => setOpen(false)}>
        <HvDialogTitle>
          {t("properties.upload.previewDialogTitle")}
        </HvDialogTitle>
        <HvDialogContent>
          {attribute.contentType === "image" && (
            <img alt={name} style={previewStyle} src={itemUrl} />
          )}
          {attribute.contentType === "video" && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video style={previewStyle} controls>
              <source src={itemUrl} />
            </video>
          )}
        </HvDialogContent>
      </HvDialog>
      <HvFileUploader
        id={fileUploaderId}
        fileList={file ? [file] : []}
        acceptedFiles={[
          (contentType || type) === "image" ? "image/*" : "video/*,.flv",
        ]}
        multiple={false}
        disabled={!!file}
        labels={{
          dropzone: label,
          acceptedFiles: ` ${t(
            `properties.upload.acceptedFiles-${contentType || type}`
          )}`,
          drag: t("properties.upload.drag"),
          dropFiles: t("properties.upload.dropFiles"),
          selectFiles: t("properties.upload.selectFiles"),
          removeFileButtonLabel: t("properties.upload.removeFileButtonLabel"),
          fileSizeError: t("validation.fileSizeNotValid"),
          fileTypeError: t("validation.fileTypeNotValid"),
        }}
        onFilesAdded={(newFiles) =>
          newFiles.forEach((newFile) => addFile(newFile, attribute))
        }
        onFileRemoved={removeFile}
        aria-errormessage={`${fileUploaderId}-error`}
      />
      {!attribute.value &&
        parseStatus(`properties.${attribute.order}.value`) !== "valid" && (
          <HvWarningText id={`${fileUploaderId}-error`} disableBorder isVisible>
            {parseStatusMessage(`properties.${order}.value`)}
          </HvWarningText>
        )}
    </>
  );
};

export default PropertyFileUpload;
